import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  CheckCircle as OkIcon,
  Warning as WarningIcon,
  Error as OutOfControlIcon,
  Science as NoQcIcon,
  HourglassEmpty as PendingIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  RuleFolder as RuleIcon,
} from '@mui/icons-material';
import qcService, {
  BatchQcPreviewResponse,
  BatchQcPreviewSample,
} from '../../services/qcService';

interface Props {
  /** All sample UUIDs in the lot the supervisor is reviewing. */
  sampleIds: string[];
  /** Display title (defaults to "Contrôle Qualité (QA/QC)"). */
  title?: string;
  /** Optional fixed batch label shown next to the title. */
  batchNumber?: string;
}

type Severity = 'IN_CONTROL' | 'WARNING' | 'OUT_OF_CONTROL' | 'NO_QC' | 'PENDING';

const STATUS_META: Record<
  Severity,
  { label: string; color: 'success' | 'warning' | 'error' | 'default' | 'info'; icon: React.ReactNode }
> = {
  IN_CONTROL:     { label: 'OK',              color: 'success', icon: <OkIcon fontSize="small" /> },
  WARNING:        { label: 'Alerte',          color: 'warning', icon: <WarningIcon fontSize="small" /> },
  OUT_OF_CONTROL: { label: 'Hors contrôle',   color: 'error',   icon: <OutOfControlIcon fontSize="small" /> },
  NO_QC:          { label: 'Échantillon',     color: 'default', icon: <NoQcIcon fontSize="small" /> },
  PENDING:        { label: 'En attente',      color: 'info',    icon: <PendingIcon fontSize="small" /> },
};

const ROLE_LABEL: Record<string, string> = {
  ECHANTILLON: 'Échantillon',
  BLANC: 'Blanc',
  BIS: 'Duplicata',
  MRC: 'MRC',
};

/**
 * Lot-level QC preview shown inside the supervisor's validation modal. It
 * fetches the per-sample QC status from `/qc/batch-preview`, renders a roll-up
 * banner ("✅ Lot conforme" / "⚠️ 1 MRC en avertissement, 1 BIS hors contrôle")
 * and a detail table listing each QC sample, its detected violations, and the
 * underlying control chart points. The supervisor can also force a recompute
 * (POST with recompute=true) when a lot predates the analyst-submit hook or
 * was edited and needs to be re-evaluated.
 */
export const LotQcPreviewPanel: React.FC<Props> = ({
  sampleIds,
  title = 'Contrôle Qualité (QA/QC)',
  batchNumber,
}) => {
  const theme = useTheme();
  const [data, setData] = useState<BatchQcPreviewResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [recomputing, setRecomputing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [expandedSection, setExpandedSection] = useState(true);

  const stableKey = useMemo(() => sampleIds.slice().sort().join(','), [sampleIds]);

  const load = useCallback(
    async (recompute = false) => {
      if (sampleIds.length === 0) {
        setData(null);
        setLoading(false);
        return;
      }
      try {
        recompute ? setRecomputing(true) : setLoading(true);
        setError(null);
        const res = await qcService.getBatchPreview(sampleIds, recompute);
        setData(res);
      } catch (e: any) {
        setError(e?.message || 'Erreur de chargement du QA/QC');
      } finally {
        setLoading(false);
        setRecomputing(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stableKey],
  );

  useEffect(() => {
    load(false);
  }, [load]);

  const toggleRow = (uuid: string) => {
    setExpandedRows((s) => {
      const next = new Set(s);
      next.has(uuid) ? next.delete(uuid) : next.add(uuid);
      return next;
    });
  };

  const summary = data?.summary;
  const samples = data?.samples ?? [];
  const qcOnly = samples.filter((s) => s.role !== 'ECHANTILLON');

  // Strong (non-blocking) warning: an MRC that has been analysed but produced
  // NO recovery point usually means the analytical method has no matching OREAS
  // digestion (the engine skips the point rather than compare against a wrong
  // certified value). Surface it so the supervisor can have the digestion set
  // on the "Gestion des Méthodes" page — without blocking validation.
  const mrcMethodIssues = qcOnly.filter(
    (s) =>
      s.role === 'MRC' &&
      s.status !== 'PENDING' &&
      !(s.points || []).some((p) => p.control_type === 'CRM_RECOVERY'),
  );

  /* ─────────────────────────────── Banner ─────────────────────────────── */

  const renderBanner = () => {
    if (!summary) return null;
    const { overall, qc_samples, in_control, warning, out_of_control, pending } = summary;

    if (overall === 'NO_QC_TAGGED') {
      return (
        <Alert severity="info" icon={<NoQcIcon />}>
          <AlertTitle>Aucun contrôle QA/QC dans ce lot</AlertTitle>
          Ce lot ne contient aucun échantillon de type BLANC, BIS ou MRC tagué à la
          réception. Le lot peut être validé sans vérification de contrôle qualité.
        </Alert>
      );
    }

    if (overall === 'OK') {
      return (
        <Alert severity="success" icon={<OkIcon />}>
          <AlertTitle>Lot conforme — {qc_samples} contrôle{qc_samples > 1 ? 's' : ''} QA/QC en limites</AlertTitle>
          Tous les échantillons de contrôle (BLANC / BIS / MRC) sont dans les limites
          attendues. Validation possible.
        </Alert>
      );
    }

    if (overall === 'WARNING') {
      return (
        <Alert severity="warning" icon={<WarningIcon />}>
          <AlertTitle>
            ⚠️ Avertissements détectés — {warning} contrôle{warning > 1 ? 's' : ''} en zone d'alerte
            {in_control > 0 ? ` (${in_control} OK)` : ''}
            {pending > 0 ? `, ${pending} en attente` : ''}
          </AlertTitle>
          <Box component="ul" sx={{ mt: 0.5, mb: 0, pl: 3 }}>
            {summary.violation_messages.map((m, i) => (
              <li key={i}>
                <Typography variant="body2">{m}</Typography>
              </li>
            ))}
          </Box>
        </Alert>
      );
    }

    return (
      <Alert severity="error" icon={<OutOfControlIcon />}>
        <AlertTitle>
          ⛔ Lot hors contrôle — {out_of_control} contrôle{out_of_control > 1 ? 's' : ''} en dépassement
          {warning > 0 ? `, ${warning} en avertissement` : ''}
          {in_control > 0 ? `, ${in_control} OK` : ''}
        </AlertTitle>
        <Box component="ul" sx={{ mt: 0.5, mb: 0, pl: 3 }}>
          {summary.violation_messages.map((m, i) => (
            <li key={i}>
              <Typography variant="body2">{m}</Typography>
            </li>
          ))}
        </Box>
        <Typography variant="caption" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
          Vérifiez la cause (contamination, dérive de l'instrument, erreur de pesée…)
          avant de valider ou d'approuver le lot.
        </Typography>
      </Alert>
    );
  };

  /* ────────────────────────────── Row detail ───────────────────────────── */

  const renderRoleChip = (s: BatchQcPreviewSample) => {
    if (s.role === 'ECHANTILLON') {
      return <Chip size="small" label={ROLE_LABEL[s.role]} variant="outlined" />;
    }
    let extra = '';
    if (s.role === 'BIS' && s.parent_sample_human_id) {
      extra = ` (de ${s.parent_sample_human_id})`;
    } else if (s.role === 'MRC' && s.reference_material_code) {
      extra = ` — ${s.reference_material_code}`;
    } else if (s.role === 'BLANC' && s.blanc_subtype) {
      // Make the prep-mécanique distinction obvious — these blancs are
      // contamination / mass-loss checks, not analytical reagent blanks.
      extra =
        s.blanc_subtype.toUpperCase() === 'PREPARATION'
          ? ' (préparation mécanique)'
          : ` (${s.blanc_subtype.toLowerCase()})`;
    }
    return (
      <Chip
        size="small"
        color={s.role === 'MRC' ? 'primary' : s.role === 'BIS' ? 'secondary' : 'default'}
        label={`${ROLE_LABEL[s.role] || s.role}${extra}`}
      />
    );
  };

  // Map PREP_* control types to a human-friendly label for the points table.
  // Other types fall back to the existing single-word labels.
  const prettyControlType = (
    controlType: string,
  ): { label: string; isPrep: boolean } => {
    switch (controlType) {
      case 'BLANK':
        return { label: 'Blanc', isPrep: false };
      case 'DUPLICATE_RPD':
        return { label: 'RPD', isPrep: false };
      case 'CRM_RECOVERY':
        return { label: 'Récup. MRC', isPrep: false };
      case 'PREP_CONCASSAGE_BLANK':
        return { label: 'Blanc Concassage', isPrep: true };
      case 'PREP_BROYAGE_BLANK':
        return { label: 'Blanc Broyage', isPrep: true };
      case 'PREP_CONCASSAGE_QUALITY':
        return { label: 'Qualité de concassage', isPrep: true };
      case 'PREP_BROYAGE_QUALITY':
        return { label: 'Qualité de broyage', isPrep: true };
      default:
        return { label: controlType, isPrep: false };
    }
  };

  const prettyElement = (element: string): string =>
    element === 'MASS_LOSS' ? 'Perte de masse' : element;

  const renderStatusChip = (status: Severity) => {
    const meta = STATUS_META[status];
    return (
      <Chip
        size="small"
        color={meta.color as any}
        icon={meta.icon as any}
        label={meta.label}
        variant={status === 'IN_CONTROL' ? 'outlined' : 'filled'}
      />
    );
  };

  /* ────────────────────────────── Render ─────────────────────────────── */

  if (sampleIds.length === 0) {
    return null;
  }

  if (loading && !data) {
    return (
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" gap={1}>
            <CircularProgress size={18} />
            <Typography variant="body2">Chargement du contrôle qualité…</Typography>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert severity="warning" sx={{ mb: 2 }} action={
        <Button size="small" onClick={() => load(false)} startIcon={<RefreshIcon />}>
          Réessayer
        </Button>
      }>
        Impossible de charger le contrôle qualité : {error}
      </Alert>
    );
  }

  return (
    <Card sx={{ mb: 2, border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}` }}>
      <CardContent sx={{ p: 2 }}>
        {/* Header */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 1.5 }}
        >
          <Stack direction="row" alignItems="center" gap={1.2}>
            <RuleIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.05rem' }}>
              {title}
            </Typography>
            {batchNumber && (
              <Chip size="small" label={batchNumber} variant="outlined" />
            )}
            {summary && summary.qc_samples > 0 && (
              <Chip
                size="small"
                color="info"
                label={`${summary.qc_samples} contrôle${summary.qc_samples > 1 ? 's' : ''} QA/QC`}
              />
            )}
          </Stack>
          <Stack direction="row" gap={1}>
            <Tooltip title="Forcer le recalcul du QA/QC pour les échantillons de ce lot">
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={recomputing ? <CircularProgress size={14} /> : <RefreshIcon />}
                  onClick={() => load(true)}
                  disabled={recomputing}
                >
                  Recalculer
                </Button>
              </span>
            </Tooltip>
            <IconButton size="small" onClick={() => setExpandedSection((v) => !v)}>
              {expandedSection ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Stack>
        </Stack>

        {/* Banner */}
        {renderBanner()}

        {/* OREAS method/digestion mismatch warning (non-blocking) */}
        {mrcMethodIssues.length > 0 && (
          <Alert severity="warning" sx={{ mt: 1.5 }} icon={<WarningIcon />}>
            <AlertTitle>
              Récupération MRC non calculée — {mrcMethodIssues.length} contrôle
              {mrcMethodIssues.length > 1 ? 's' : ''} MRC
            </AlertTitle>
            <Typography variant="body2">
              {mrcMethodIssues
                .map((s) => `${s.sample_human_id}${s.reference_material_code ? ` (${s.reference_material_code})` : ''}`)
                .join(', ')}
              {' '}— aucune valeur certifiée OREAS ne correspond à la digestion de la
              méthode utilisée. Vérifiez le champ « Attaque / Digestion (OREAS) » de la
              méthode dans <strong>Gestion des Méthodes</strong>, ou que le MRC couvre
              bien cette attaque. La validation reste possible.
            </Typography>
          </Alert>
        )}

        <Collapse in={expandedSection}>
          {/* Per-sample table (only QC samples, regular ones are hidden by default) */}
          {qcOnly.length > 0 && (
            <TableContainer sx={{ mt: 2, maxHeight: 420 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, backgroundColor: '#f5f5f5' }}>Échantillon</TableCell>
                    <TableCell sx={{ fontWeight: 700, backgroundColor: '#f5f5f5' }}>Rôle QC</TableCell>
                    <TableCell sx={{ fontWeight: 700, backgroundColor: '#f5f5f5' }}>Statut</TableCell>
                    <TableCell sx={{ fontWeight: 700, backgroundColor: '#f5f5f5' }}>Détails / Violations</TableCell>
                    <TableCell sx={{ fontWeight: 700, backgroundColor: '#f5f5f5', width: 48 }}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {qcOnly.map((s) => {
                    const opened = expandedRows.has(s.sample_uuid);
                    return (
                      <React.Fragment key={s.sample_uuid}>
                        <TableRow
                          hover
                          sx={{
                            backgroundColor:
                              s.status === 'OUT_OF_CONTROL'
                                ? alpha(theme.palette.error.main, 0.06)
                                : s.status === 'WARNING'
                                ? alpha(theme.palette.warning.main, 0.07)
                                : 'inherit',
                          }}
                        >
                          <TableCell sx={{ fontWeight: 600 }}>{s.sample_human_id}</TableCell>
                          <TableCell>{renderRoleChip(s)}</TableCell>
                          <TableCell>{renderStatusChip(s.status)}</TableCell>
                          <TableCell>
                            {s.reasons.length === 0 ? (
                              <Typography variant="body2" color="text.secondary">
                                {s.status === 'IN_CONTROL'
                                  ? 'Tous les éléments dans les limites'
                                  : '—'}
                              </Typography>
                            ) : (
                              <Stack gap={0.3}>
                                {s.reasons.slice(0, 3).map((r, i) => (
                                  <Typography key={i} variant="body2">
                                    • {r}
                                  </Typography>
                                ))}
                                {s.reasons.length > 3 && (
                                  <Typography variant="caption" color="text.secondary">
                                    +{s.reasons.length - 3} autres…
                                  </Typography>
                                )}
                              </Stack>
                            )}
                          </TableCell>
                          <TableCell>
                            {s.points.length > 0 && (
                              <IconButton size="small" onClick={() => toggleRow(s.sample_uuid)}>
                                {opened ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                              </IconButton>
                            )}
                          </TableCell>
                        </TableRow>
                        {opened && (
                          <TableRow>
                            <TableCell colSpan={5} sx={{ backgroundColor: '#fafafa', py: 1 }}>
                              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                Points de carte de contrôle ({s.points.length})
                              </Typography>
                              <Table size="small" sx={{ mt: 0.5 }}>
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Élément</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell>Valeur</TableCell>
                                    <TableCell>Moyenne</TableCell>
                                    <TableCell>LCL / UCL</TableCell>
                                    <TableCell>LWL / UWL</TableCell>
                                    <TableCell>Statut</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {s.points.map((p) => {
                                    const ct = prettyControlType(p.control_type);
                                    const fmt = (n: number | string) =>
                                      ct.isPrep
                                        ? `${Number(n).toFixed(2)} %`
                                        : Number(n).toFixed(3);
                                    return (
                                      <TableRow key={p.id}>
                                        <TableCell>{prettyElement(p.element)}</TableCell>
                                        <TableCell>
                                          <Chip
                                            size="small"
                                            variant="outlined"
                                            color={ct.isPrep ? 'warning' : 'default'}
                                            label={ct.label}
                                          />
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>
                                          {fmt(p.value)}
                                        </TableCell>
                                        <TableCell>{fmt(p.mean)}</TableCell>
                                        <TableCell>
                                          {fmt(p.lcl)} / {fmt(p.ucl)}
                                        </TableCell>
                                        <TableCell>
                                          {fmt(p.lwl)} / {fmt(p.uwl)}
                                        </TableCell>
                                        <TableCell>
                                          {p.out_of_control ? (
                                            <Chip size="small" color="error" label="OOC" />
                                          ) : p.warning_level ? (
                                            <Chip size="small" color="warning" label="Alerte" />
                                          ) : (
                                            <Chip size="small" color="success" variant="outlined" label="OK" />
                                          )}
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Helpful hint when there are pending samples */}
          {summary && summary.pending > 0 && (
            <Alert severity="info" sx={{ mt: 1 }}>
              {summary.pending} échantillon{summary.pending > 1 ? 's' : ''} QC en attente
              de calcul. Cliquez sur <strong>Recalculer</strong> pour exécuter le moteur QA/QC
              maintenant.
            </Alert>
          )}
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default LotQcPreviewPanel;
